/**
 * What a rating holds.
 *
 * Pure, so the field configuration can read the defaults without pulling an
 * icon set in with them. What the symbols look like is in `ui/rating.tsx`.
 */
import type { Field, FieldOf, FieldPropertiesOf } from '@/api/types'
import { SELECT_COLORS, type SelectColor } from './select-options'

/** The style a rating is drawn in, as the request schema has it: a checked choice. */
export type RatingStyle = NonNullable<FieldPropertiesOf<'rating'>['style']>

/**
 * The five, in the order they are offered.
 *
 * Written out rather than derived, because a type is not a list -- and checked
 * against the type, so a sixth added to the backend is a compile error here
 * rather than a style nobody can pick.
 */
export const RATING_STYLES = [
  'star',
  'heart',
  'thumbs-up',
  'flag',
  'smile',
] as const satisfies readonly RatingStyle[]

export const DEFAULT_RATING_STYLE: RatingStyle = 'star'
/** The backend's own default, which is what an older field carries. */
export const DEFAULT_RATING_COLOR = 'dark-orange'
const DEFAULT_RATING_MAX = 5

/**
 * The style a field is drawn in.
 *
 * No guard against an unknown one: `style` is a checked choice on both sides of
 * the request, and the symbol table is exhaustive over that choice, so a sixth
 * style added to the backend fails to compile here rather than arriving at
 * runtime as something to fall back from.
 */
export function ratingStyleOf(field: Field): RatingStyle {
  return (field as FieldOf<'rating'>).style ?? DEFAULT_RATING_STYLE
}

/**
 * The colour the symbols are drawn in.
 *
 * The same palette a select option is coloured from. The backend stores this as
 * a free string of up to fifty characters rather than a checked choice, so a
 * name nothing recognises has to render as something -- and what it renders as
 * is the colour a field nobody has configured is already carrying.
 */
export function ratingColorValue(color: unknown): string {
  return SELECT_COLORS[color as SelectColor] ?? SELECT_COLORS[DEFAULT_RATING_COLOR]
}

export function ratingColor(field: Field): string {
  return ratingColorValue((field as FieldOf<'rating'>).color)
}

/**
 * How many points the field allows.
 *
 * Only the absent case is defended against, and only because the schema makes
 * the property optional. The backend holds it between one and ten, so there is
 * no zero or negative maximum to guard -- and a guard nothing can reach is a
 * guard no test can tell from its absence.
 */
export function ratingMaxOf(field: Field): number {
  const max = (field as FieldOf<'rating'>).max_value
  return typeof max === 'number' ? max : DEFAULT_RATING_MAX
}

/**
 * A rating read out of whatever arrived, clamped to what the field can hold.
 *
 * Clamped rather than refused because the backend clamps too: a paste of "9"
 * into a field that goes up to five stores five, and an editor that sent nine
 * would show a value that changed the moment the answer came back.
 */
export function parseRating(value: unknown, max: number): number {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number.parseInt(value, 10)
        : Number.NaN
  if (!Number.isFinite(parsed)) return 0
  return Math.min(Math.max(Math.trunc(parsed), 0), max)
}
